import { AsyncDataApi,
    AsyncTicketCreatedRepresentation,
    AsyncTicketsApi,
    ChartDataRepresentationFromJSON, Configuration, DataApi as DataRestApi,
    GaugeDataRepresentationFromJSON, OperatorPrognosisMonetaryRepresentation,
    OperatorPrognosisMonetaryRequest, PieDataRepresentationFromJSON,
    PowercurveDataRepresentationFromJSON,
    ScatterDataRepresentationFromJSON,
    TicketRepresentation,
    TicketStatus, UploadCSVDataTypeEnum } from '@/clients/dashboardapi/v2';
import { IndexedDBRepository } from '@/modules/shared/indexeddb';
import type { AuthApi } from '@/modules/shared/adapter/AuthApi';
import type {
    ChartDataRepresentation,
    ChartDataRequest,
    DataApi,
    GaugeDataRepresentation,
    GaugeDataRequest,
    LatestLogDataRequest,
    LogDataRepresentation,
    LogDataRequest,
    PieDataRepresentation,
    PieDataRequest,
    PowercurveDataRepresentation,
    PowercurveDataRequest,
    ScatterDataRepresentation,
    ScatterDataRequest,
    WindroseDataRepresentation,
    WindroseDataRequest,
} from '@/modules/shared/adapter/DataApi';
import { AsyncDebouncer, AuthMiddleware, ConnectionResetMiddleware } from '../middleware';

interface DataCacheEntry {
    key: string;
    data: any;
}

export class CachedDataRestApi implements DataApi {
    private static readonly chartPollingIntervalMs = 2_000;

    private readonly auth: AuthApi;
    private readonly asyncDataApi: AsyncDataApi;
    private readonly asyncTicketsApi: AsyncTicketsApi;
    private readonly dataApi: DataRestApi;
    private readonly dataCache: IndexedDBRepository<string, DataCacheEntry>;

    constructor(indexedDb: Promise<IDBDatabase>, apis: { auth: AuthApi }) {
        const restApiConfig = new Configuration({
            accessToken: () => apis.auth.getAuthToken(),
            basePath: `${process.env.VUE_APP_SERVICE_API}v2`,
            credentials: 'include',
            middleware: [new AuthMiddleware(apis.auth), new ConnectionResetMiddleware()],
        });
        this.auth = apis.auth;
        this.asyncDataApi = new AsyncDataApi(restApiConfig);
        this.asyncTicketsApi = new AsyncTicketsApi(restApiConfig);
        this.dataApi = new DataRestApi(restApiConfig);
        this.dataCache = new IndexedDBRepository<string, DataCacheEntry>(indexedDb, 'data', {
            invalidateOnReload: true,
            invalidateOnUserChanged: false,
            invalidateOnInterval: CachedDataRestApi.maxCacheTime(),
        });
    }

    public async getTimeSeries(request: ChartDataRequest): Promise<ChartDataRepresentation[]> {
        return this.fetchTimeSeries(request);
    }

    public async getTimeSeriesAsCSV(request: ChartDataRequest): Promise<Blob> {
        return this.dataApi.getChartDataAsCSV(request);
    }

    public async getNumeric(request: GaugeDataRequest): Promise<GaugeDataRepresentation> {
        return this.fetchGauge(request);
    }

    public async getDistribution(request: PieDataRequest): Promise<PieDataRepresentation> {
        return this.fetchPie(request);
    }

    public async getWindrose(request: WindroseDataRequest): Promise<WindroseDataRepresentation> {
        return this.fetchData(request, (r) => this.dataApi.getWindroseData(r));
    }

    public async getScatter(request: ScatterDataRequest): Promise<ScatterDataRepresentation[]> {
        return this.fetchScatter(request);
    }

    public async getPowerCurve(request: PowercurveDataRequest): Promise<PowercurveDataRepresentation[]> {
        return this.fetchPowerCurve(request);
    }

    public async getLatestLogs(request: LatestLogDataRequest): Promise<LogDataRepresentation[]> {
        return this.fetchData(request, (r) => this.dataApi.getLatestLogData(r));
    }

    public async getLogs(request: LogDataRequest): Promise<ReadableStream<Uint8Array>> {
        const response = await this.dataApi.getLogDataRaw({ logDataRequest: request });

        if (!response.raw.body) {
            throw new Error('ReadableStream not supported or no body in response');
        }

        return response.raw.body;
    }

    public async getLogsAsCSV(logDataRequest: LogDataRequest): Promise<Blob> {
        console.log('Generating CSV: [__________________________________________________] 0%');
        const url = `${process.env.VUE_APP_SERVICE_API}v2/data/csv/log`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': await this.auth.getAuthToken(),
            },
            body: JSON.stringify(logDataRequest),
        });

        const reader = response.body?.getReader();
        // this header is only accessible when frontend and backend are on the same domain!
        const generatorCount = parseInt(response.headers.get('X-Generator-Count') || '768', 10);
        const stream = new ReadableStream({
            async start(controller) {
                try {
                    const receivedGenerators: Set<string> = new Set<string>();
                    const decoder = new TextDecoder();
                    const encoder = new TextEncoder();
                    let lastPrintedProgress = 0;
                    let result = await reader?.read();
                    while (result && !result.done) {
                        let content = decoder
                            .decode(result.value)
                            // split content down to single csv cells
                            .split('\n')
                            .filter((line) => line.trim().length > 0)
                            .map((line) => line.split(';'))
                            .map((cells) => {
                                receivedGenerators.add(`${cells[0]} / ${cells[1]}`);
                                return cells;
                            })
                            // filter out dummy rows without data
                            .filter((cells) => cells[3] && cells[4])
                            .map((cells) => cells.join(';'))
                            .join('\n');
                        if (content.length > 0) {
                            // add newline after each block of log entries
                            content = `${content}\n`;
                        }
                        controller.enqueue(encoder.encode(content));
                        const progress = Math.floor((receivedGenerators.size / generatorCount) * 100);
                        if (progress > lastPrintedProgress) {
                            lastPrintedProgress = progress;
                            let out = 'Generating CSV: [';
                            for (let i = 1; i <= 100; i += 2) {
                                out = i < progress ? `${out}#` : `${out}_`;
                            }
                            out = `${out}] ${progress}% (${receivedGenerators.size}/${generatorCount})`;
                            console.log(out);
                        }

                        result = await reader?.read();
                    }
                    controller.close();
                } catch (e) {
                    controller.error(e);
                }
            },
        });
        return new Response(stream).blob();
    }

    public async uploadDataCSV(type: UploadCSVDataTypeEnum, blob: Blob): Promise<string> {
        return this.dataApi.uploadCSVData(type, blob);
    }

    public async uploadDataCSV2(type: UploadCSVDataTypeEnum, blob: Blob, year: number): Promise<string> {
        return this.dataApi.uploadCSVData(type, blob, year);
    }

    public async getOperatorPrognosisMonetary(request: OperatorPrognosisMonetaryRequest): Promise<OperatorPrognosisMonetaryRepresentation> {
        return this.fetchData(request, () => this.dataApi.getOperatorPrognosisMonetary(request));
    }

    private async fetchTimeSeries(request: ChartDataRequest): Promise<ChartDataRepresentation[]> {
        return await this.fetchData(request, (r) => this.fetchTimeSeriesAsync(r));
    }

    private async fetchPowerCurve(request: PowercurveDataRequest): Promise<PowercurveDataRepresentation[]> {
        return await this.fetchData(request, (r) => this.fetchPowerCurveAsync(r));
    }

    private async fetchGauge(request: GaugeDataRequest): Promise<GaugeDataRepresentation> {
        return await this.fetchData(request, (r) => this.fetchGaugeAsync(r));
    }

    private async fetchPie(request: PieDataRequest): Promise<PieDataRepresentation> {
        return await this.fetchData(request, (r) => this.fetchPieAsync(r));
    }

    private async fetchScatter(request: ScatterDataRequest): Promise<ScatterDataRepresentation[]> {
        return await this.fetchData(request, (r) => this.fetchScatterAsync(r));
    }

    private async fetchTimeSeriesAsync(request: ChartDataRequest): Promise<ChartDataRepresentation[]> {
        const ticket = await this.asyncDataApi.startChartDataAsync(request);
        if (!ticket.ticketId) {
            throw CachedDataRestApi.createHttpError('Async chart request did not return a ticket id.');
        }
        return this.pollTimeSeriesTicket(ticket);
    }

    private async pollTimeSeriesTicket(ticket: AsyncTicketCreatedRepresentation): Promise<ChartDataRepresentation[]> {
        // The backend controls completion time, so polling continues until the ticket reaches a terminal state.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const ticketState = await this.asyncTicketsApi.getAsyncTicket(ticket.ticketId!);
            switch (ticketState.status) {
                case TicketStatus.Done:
                    return this.mapTimeSeriesTicketPayload(ticketState);
                case TicketStatus.Failed:
                    throw CachedDataRestApi.createHttpError(ticketState.error?.message || 'Async chart request failed.');
                case TicketStatus.Expired:
                    throw CachedDataRestApi.createHttpError('Async chart ticket expired before the result was available.');
                case TicketStatus.Pending:
                case TicketStatus.Running:
                case undefined:
                    await new Promise((resolve) => { setTimeout(resolve, CachedDataRestApi.chartPollingIntervalMs); });
                    break;
                default:
                    throw CachedDataRestApi.createHttpError(`Unknown async chart ticket status: ${ticketState.status}`);
            }
        }
    }

    private async fetchPowerCurveAsync(request: PowercurveDataRequest): Promise<PowercurveDataRepresentation[]> {
        const ticket = await this.asyncDataApi.startPowercurveDataAsync(request);
        if (!ticket.ticketId) {
            throw CachedDataRestApi.createHttpError('Async powercurve request did not return a ticket id.');
        }
        return this.pollPowerCurveTicket(ticket);
    }

    private mapTimeSeriesTicketPayload(ticket: TicketRepresentation): ChartDataRepresentation[] {
        const payload = ticket.result?.payload;
        if (!Array.isArray(payload)) {
            throw CachedDataRestApi.createHttpError('Async chart ticket did not return a valid payload.');
        }
        return payload.map((entry) => ChartDataRepresentationFromJSON(entry));
    }

    private async pollPowerCurveTicket(ticket: AsyncTicketCreatedRepresentation): Promise<PowercurveDataRepresentation[]> {
        // The backend controls completion time, so polling continues until the ticket reaches a terminal state.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const ticketState = await this.asyncTicketsApi.getAsyncTicket(ticket.ticketId!);
            switch (ticketState.status) {
                case TicketStatus.Done:
                    return this.mapPowerCurveTicketPayload(ticketState);
                case TicketStatus.Failed:
                    throw CachedDataRestApi.createHttpError(ticketState.error?.message || 'Async powercurve request failed.');
                case TicketStatus.Expired:
                    throw CachedDataRestApi.createHttpError('Async powercurve ticket expired before the result was available.');
                case TicketStatus.Pending:
                case TicketStatus.Running:
                case undefined:
                    await new Promise((resolve) => { setTimeout(resolve, CachedDataRestApi.chartPollingIntervalMs); });
                    break;
                default:
                    throw CachedDataRestApi.createHttpError(`Unknown async powercurve ticket status: ${ticketState.status}`);
            }
        }
    }

    private mapPowerCurveTicketPayload(ticket: TicketRepresentation): PowercurveDataRepresentation[] {
        const payload = ticket.result?.payload;
        if (!Array.isArray(payload)) {
            throw CachedDataRestApi.createHttpError('Async powercurve ticket did not return a valid payload.');
        }
        return payload.map((entry) => PowercurveDataRepresentationFromJSON(entry));
    }

    private async fetchGaugeAsync(request: GaugeDataRequest): Promise<GaugeDataRepresentation> {
        const ticket = await this.asyncDataApi.startGaugeDataAsync(request);
        if (!ticket.ticketId) {
            throw CachedDataRestApi.createHttpError('Async gauge request did not return a ticket id.');
        }
        return this.pollGaugeTicket(ticket);
    }

    private async pollGaugeTicket(ticket: AsyncTicketCreatedRepresentation): Promise<GaugeDataRepresentation> {
        // The backend controls completion time, so polling continues until the ticket reaches a terminal state.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const ticketState = await this.asyncTicketsApi.getAsyncTicket(ticket.ticketId!);
            switch (ticketState.status) {
                case TicketStatus.Done:
                    return this.mapGaugeTicketPayload(ticketState);
                case TicketStatus.Failed:
                    throw CachedDataRestApi.createHttpError(ticketState.error?.message || 'Async gauge request failed.');
                case TicketStatus.Expired:
                    throw CachedDataRestApi.createHttpError('Async gauge ticket expired before the result was available.');
                case TicketStatus.Pending:
                case TicketStatus.Running:
                case undefined:
                    await new Promise((resolve) => { setTimeout(resolve, CachedDataRestApi.chartPollingIntervalMs); });
                    break;
                default:
                    throw CachedDataRestApi.createHttpError(`Unknown async gauge ticket status: ${ticketState.status}`);
            }
        }
    }

    private mapGaugeTicketPayload(ticket: TicketRepresentation): GaugeDataRepresentation {
        const payload = ticket.result?.payload;
        if (!payload || typeof payload !== 'object') {
            throw CachedDataRestApi.createHttpError('Async gauge ticket did not return a valid payload.');
        }
        return GaugeDataRepresentationFromJSON(payload);
    }

    private async fetchPieAsync(request: PieDataRequest): Promise<PieDataRepresentation> {
        const ticket = await this.asyncDataApi.startPieDataAsync(request);
        if (!ticket.ticketId) {
            throw CachedDataRestApi.createHttpError('Async pie request did not return a ticket id.');
        }
        return this.pollPieTicket(ticket);
    }

    private async pollPieTicket(ticket: AsyncTicketCreatedRepresentation): Promise<PieDataRepresentation> {
        // The backend controls completion time, so polling continues until the ticket reaches a terminal state.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const ticketState = await this.asyncTicketsApi.getAsyncTicket(ticket.ticketId!);
            switch (ticketState.status) {
                case TicketStatus.Done:
                    return this.mapPieTicketPayload(ticketState);
                case TicketStatus.Failed:
                    throw CachedDataRestApi.createHttpError(ticketState.error?.message || 'Async pie request failed.');
                case TicketStatus.Expired:
                    throw CachedDataRestApi.createHttpError('Async pie ticket expired before the result was available.');
                case TicketStatus.Pending:
                case TicketStatus.Running:
                case undefined:
                    await new Promise((resolve) => { setTimeout(resolve, CachedDataRestApi.chartPollingIntervalMs); });
                    break;
                default:
                    throw CachedDataRestApi.createHttpError(`Unknown async pie ticket status: ${ticketState.status}`);
            }
        }
    }

    private mapPieTicketPayload(ticket: TicketRepresentation): PieDataRepresentation {
        const payload = ticket.result?.payload;
        if (!payload || typeof payload !== 'object') {
            throw CachedDataRestApi.createHttpError('Async pie ticket did not return a valid payload.');
        }
        return PieDataRepresentationFromJSON(payload);
    }

    private async fetchScatterAsync(request: ScatterDataRequest): Promise<ScatterDataRepresentation[]> {
        const ticket = await this.asyncDataApi.startScatterDataAsync(request);
        if (!ticket.ticketId) {
            throw CachedDataRestApi.createHttpError('Async scatter request did not return a ticket id.');
        }
        return this.pollScatterTicket(ticket);
    }

    private async pollScatterTicket(ticket: AsyncTicketCreatedRepresentation): Promise<ScatterDataRepresentation[]> {
        // The backend controls completion time, so polling continues until the ticket reaches a terminal state.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const ticketState = await this.asyncTicketsApi.getAsyncTicket(ticket.ticketId!);
            switch (ticketState.status) {
                case TicketStatus.Done:
                    return this.mapScatterTicketPayload(ticketState);
                case TicketStatus.Failed:
                    throw CachedDataRestApi.createHttpError(ticketState.error?.message || 'Async scatter request failed.');
                case TicketStatus.Expired:
                    throw CachedDataRestApi.createHttpError('Async scatter ticket expired before the result was available.');
                case TicketStatus.Pending:
                case TicketStatus.Running:
                case undefined:
                    await new Promise((resolve) => { setTimeout(resolve, CachedDataRestApi.chartPollingIntervalMs); });
                    break;
                default:
                    throw CachedDataRestApi.createHttpError(`Unknown async scatter ticket status: ${ticketState.status}`);
            }
        }
    }

    private mapScatterTicketPayload(ticket: TicketRepresentation): ScatterDataRepresentation[] {
        const payload = ticket.result?.payload;
        if (!Array.isArray(payload)) {
            throw CachedDataRestApi.createHttpError('Async scatter ticket did not return a valid payload.');
        }
        return payload.map((entry) => ScatterDataRepresentationFromJSON(entry));
    }

    private fetchData<R, T>(request: R, cb: (request: R) => Promise<T>) {
        const hash = CachedDataRestApi.hashDataRequest(request);
        return AsyncDebouncer.debounce(`CachedDataRestApi.${hash}`, async () => {
            const cachedData = await this.dataCache.findByKey(hash);
            if (cachedData) {
                return cachedData.data as T;
            }
            const data = await cb(request);
            await this.dataCache.save({ key: hash, data: data });
            return data;
        });
    }

    private static hashDataRequest(object: any): string {
        const str = JSON.stringify(object);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + char;
            // eslint-disable-next-line no-bitwise
            hash &= hash; // Convert to 32bit integer
        }
        if (hash < 0) {
            return `1${(hash * -1).toString(16)}`;
        }
        return hash.toString(16);
    }

    private static maxCacheTime(): number {
        let seconds = 300;
        if (process.env.VUE_APP_MAX_DATA_CACHE) {
            try {
                seconds = parseInt(process.env.VUE_APP_MAX_DATA_CACHE, 10);
            } catch (e) {
                console.error(`VUE_APP_MAX_DATA_CACHE has an invalid value: ${process.env.VUE_APP_MAX_DATA_CACHE}. Using default of 300s.`);
            }
        }
        // cache at least 1 second
        return 1000 * Math.max(1, seconds);
    }

    private static createHttpError(message: string, status: number = 500): Response {
        return new Response(message, {
            status: status,
            headers: { 'Content-Type': 'text/plain' },
        });
    }
}
